import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Maximize2, IndianRupee, Tag, MessageCircle, Phone, Share2, Calendar, ArrowRight, Eye, Flame, CheckCircle2, X, ChevronLeft, ChevronRight, Heart, Sparkles } from 'lucide-react';
import { getWhatsAppLink, openWhatsApp, getPhoneLink, PHONE_NUMBER_DISPLAY } from '../constants/contact';
import { INDIAN_STATES, getCitiesByState } from '../constants/locations';
import { ImageGallery } from './ImageGallery';
import { AppointmentModal } from './AppointmentModal';
import { AdvisorCallModal } from './AdvisorCallModal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MouzaMapCard } from './property/MouzaMapCard';
import { LocalityReportCard } from './property/LocalityReportCard';
import { PropertyQrCard } from './property/PropertyQrCard';
import { PropertyCard } from './property/PropertyCard';

// PropertyDetailPortal — mobile-swipe-to-close-aware wrapper around the
// property detail modal shell. Kept as a stable top-level component so it
// doesn't remount on every parent state change.
const PropertyDetailPortal = ({ close, onPrev, onNext, canPrev, canNext, children }) => {
  const [drag, setDrag] = React.useState(0);
  const startY = React.useRef(null);

  const onTouchStart = (e) => {
    const shell = e.currentTarget;
    const body = shell.querySelector('.am-property-modal-body');
    if (body && body.contains(e.target) && body.scrollTop > 0) return;
    startY.current = e.touches[0].clientY;
    setDrag(0);
  };

  const onTouchMove = (e) => {
    if (startY.current == null) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) setDrag(dy);
  };

  const onTouchEnd = () => {
    if (startY.current == null) return;
    const d = drag;
    startY.current = null;
    if (d > 120) {
      close();
    }
    setDrag(0);
  };

  // Keyboard shortcuts — Escape to close, ← / → to navigate.
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft' && canPrev && onPrev) onPrev();
      else if (e.key === 'ArrowRight' && canNext && onNext) onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close, onPrev, onNext, canPrev, canNext]);

  const shellStyle = drag > 0 ? { transform: `translateY(${drag}px)`, transition: 'none' } : undefined;

  return (
    <div
      className="am-property-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="am-property-modal-title"
      style={drag > 60 ? { opacity: Math.max(0.35, 1 - drag / 400) } : undefined}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      data-testid="property-detail-modal"
    >
      <div
        className="am-property-modal-shell"
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        style={shellStyle}
      >
        {/* Prev / Next arrows */}
        {onPrev && (
          <button
            type="button"
            className="am-property-nav-btn am-property-nav-prev"
            onClick={onPrev}
            disabled={!canPrev}
            aria-label="Previous property"
            data-testid="property-nav-prev"
            title="Previous (←)"
          >
            <ChevronLeft size={22} />
          </button>
        )}
        {onNext && (
          <button
            type="button"
            className="am-property-nav-btn am-property-nav-next"
            onClick={onNext}
            disabled={!canNext}
            aria-label="Next property"
            data-testid="property-nav-next"
            title="Next (→)"
          >
            <ChevronRight size={22} />
          </button>
        )}

        {/* Mobile grab handle — signals "swipe down to close" */}
        <div className="am-property-modal-grab" aria-hidden="true">
          <span />
        </div>
        {children}
      </div>
    </div>
  );
};

export const Projects = () => {
  const { code: routeCode } = useParams();
  const navigate = useNavigate();
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showAdvisor, setShowAdvisor] = useState(false);
  const [filter, setFilter] = useState('All');
  const [amenityFilters, setAmenityFilters] = useState(new Set());
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [showAppointment, setShowAppointment] = useState(false);
  const [appointmentProperty, setAppointmentProperty] = useState(null);
  const [inquiryStats, setInquiryStats] = useState({});   // { [property_id]: {views_today, ...} }
  const [shareCopied, setShareCopied] = useState(false);

  // Themed fallback so a broken image URL never leaves an empty grey box.
  const FALLBACK_IMG = 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200&q=80';
  const onImgError = (e) => {
    if (e?.target && e.target.src !== FALLBACK_IMG) e.target.src = FALLBACK_IMG;
  };

  const openShareableProperty = useCallback((prop) => {
    setSelectedProperty(prop);
    if (prop?.property_code && window.location.pathname !== `/property/${prop.property_code}`) {
      // Push to browser history so the URL is copy-share-able but don't reload.
      window.history.pushState({}, '', `/property/${prop.property_code}`);
    }
    setShareCopied(false);
  }, []);

  const closeShareableProperty = useCallback(() => {
    setSelectedProperty(null);
    if (window.location.pathname.startsWith('/property/')) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  // Lock body scroll + wire Escape key while the property modal is open.
  useEffect(() => {
    if (!selectedProperty) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') closeShareableProperty();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [selectedProperty, closeShareableProperty]);
  const [inquiryData, setInquiryData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Search filters
  const [searchFilters, setSearchFilters] = useState({
    state: '',
    city: '',
    type: ''
  });

  const propertyTypes = ['All', 'Residential', 'Agricultural', 'Commercial', 'Flat Rent', 'Flat Sell', 'House Rent', 'House Sell'];

  useEffect(() => {
    fetchProperties();
    
    // Listen for property type filter from navbar
    const handleFilterEvent = (e) => {
      const type = e.detail?.type;
      if (type) {
        setFilter(type);
        setSearchFilters({ state: '', city: '', type: '' });
      }
    };
    
    window.addEventListener('filterPropertyType', handleFilterEvent);
    return () => window.removeEventListener('filterPropertyType', handleFilterEvent);
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
      // Fetch live inquiry stats in parallel (best-effort, non-blocking).
      Promise.allSettled(
        response.data.map((p) => axios.get(`${API}/properties/${p.id}/inquiries`)),
      ).then((results) => {
        const map = {};
        results.forEach((r, i) => {
          if (r.status === 'fulfilled') map[response.data[i].id] = r.value.data;
        });
        setInquiryStats(map);
      });
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  // If landed on /property/<code>, auto-open the matching property once loaded.
  useEffect(() => {
    if (!routeCode || !properties.length) return;
    // If the modal is already open for this same code, do nothing —
    // otherwise this effect will race against a manual close and
    // immediately re-open the modal.
    if (selectedProperty && (selectedProperty.property_code || '').toLowerCase() === routeCode.toLowerCase()) return;
    const match = properties.find(
      (p) => (p.property_code || '').toLowerCase() === routeCode.toLowerCase(),
    );
    if (match) {
      setSelectedProperty(match);
      setTimeout(() => {
        document.getElementById('properties')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, [routeCode, properties, selectedProperty]);

  // When the user manually closes the modal, make sure the route also
  // goes back to `/` — closeShareableProperty already does this. But if
  // the browser back-nav clears routeCode while the modal is still open
  // for some other reason, sync that too.
  useEffect(() => {
    if (!routeCode && selectedProperty && window.location.pathname === '/') {
      // URL is home but modal is still up (e.g. after browser back button)
      // — leave the modal alone; user can close it via the pill.
    }
  }, [routeCode, selectedProperty]);

  const buildShareUrl = (prop) => {
    if (!prop) return window.location.origin;
    if (prop.property_code) return `${window.location.origin}/property/${prop.property_code}`;
    return `${window.location.origin}/property/${prop.id}`;
  };

  const sharePropertyViaSystem = async (prop) => {
    const url = buildShareUrl(prop);
    const title = prop?.title || 'Property on Acre Mitra';
    const text = `${title} — ${prop?.location || ''} · ${prop?.price || ''}. View on Acre Mitra:`;
    if (navigator.share) {
      try { await navigator.share({ title, text, url }); return; } catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 3000);
    } catch { /* clipboard blocked */ }
  };

  // Get cities based on selected state
  const availableCities = searchFilters.state ? getCitiesByState(searchFilters.state) : [];

  const filteredProperties = properties.filter(prop => {
    // Type filter
    if (filter !== 'All' && prop.type !== filter) return false;
    
    // Search filters
    if (searchFilters.state && !prop.location.includes(searchFilters.state)) return false;
    if (searchFilters.city && !prop.location.includes(searchFilters.city)) return false;
    if (searchFilters.type && prop.type !== searchFilters.type) return false;

    // Must-have amenity filters (chips above the grid). We combine three
    // channels — the structured flags (lift/parking), the free-form amenities
    // array set by admin, and a case-insensitive scan of the `features` list
    // — so an older listing with "Lift, Parking" in features still counts.
    if (amenityFilters.size > 0) {
      const bag = new Set(
        [
          ...(prop.amenities || []),
          ...(prop.features || []),
        ].map((s) => String(s).toLowerCase()),
      );
      for (const req of amenityFilters) {
        if (req === 'lift') {
          if (!prop.lift_available && !bag.has('lift')) return false;
        } else if (req === 'parking') {
          if (!prop.parking_available && !bag.has('parking')) return false;
        } else if (!Array.from(bag).some((s) => s.includes(req))) {
          return false;
        }
      }
    }

    return true;
  });
  
  const resetFilters = () => {
    setSearchFilters({ state: '', city: '', type: '' });
    setFilter('All');
  };
  
  const handleShare = async (property) => {
    const shareUrl = window.location.href.split('#')[0];
    const shareText = `Check out: ${property.title}\nLocation: ${property.location}\nPrice: ${property.price}\nArea: ${property.area}`;
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: property.title,
          text: shareText,
          url: shareUrl
        });
      } else {
        // Fallback: copy to clipboard
        const tempInput = document.createElement('input');
        tempInput.value = `${shareText}\n\nView: ${shareUrl}`;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
        alert('✅ Property details copied to clipboard!');
      }
    } catch (err) {
      console.error('Share failed:', err);
    }
  };

  const getPropertyWhatsAppLink = (property) => {
    const message = `Hi! I'm interested in ${property.title} located in ${property.location}. Price: ${property.price}, Area: ${property.area}. Please provide more details.`;
    return getWhatsAppLink(message);
  };

  const handleInquiry = async () => {
    if (!inquiryData.name || !inquiryData.email || !inquiryData.phone) {
      alert('⚠️ Please fill in all fields (Name, Email, Phone)');
      return;
    }

    setSubmitting(true);
    try {
      console.log('Sending property inquiry:', {
        property_id: selectedProperty.id,
        property_title: selectedProperty.title,
        user_name: inquiryData.name,
        user_email: inquiryData.email,
        user_phone: inquiryData.phone
      });

      const response = await axios.post(`${API}/property-inquiry`, {
        property_id: selectedProperty.id,
        property_title: selectedProperty.title,
        user_name: inquiryData.name,
        user_email: inquiryData.email,
        user_phone: inquiryData.phone
      });

      console.log('Inquiry response:', response);
      alert(`✅ Inquiry sent for ${selectedProperty.title}! Our team will contact you soon.`);
      setShowInquiryForm(false);
      setSelectedProperty(null);
      setInquiryData({ name: '', email: '', phone: '' });
    } catch (error) {
      console.error('Error sending inquiry:', error);
      console.error('Error details:', error.response?.data);
      alert('❌ Failed to send inquiry. Please try WhatsApp or call us at +91 8597310624');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="projects" className="py-20 bg-slate-50">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 animate-fadeIn">
          <div className="inline-block px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-4 animate-slideDown">
            Premium Plots & Land
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 animate-slideUp">
            Explore Our
            <span className="text-emerald-700"> Premium Plots</span>
          </h2>
          <p className="text-lg text-slate-600 animate-fadeIn" style={{animationDelay: '0.2s'}}>
            Handpicked selection of prime land investments across residential,
            agricultural, and commercial categories.
          </p>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 max-w-5xl mx-auto">
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">State</label>
              <Select
                value={searchFilters.state || 'ALL'}
                onValueChange={(v) =>
                  setSearchFilters({ ...searchFilters, state: v === 'ALL' ? '' : v, city: '' })
                }
              >
                <SelectTrigger className="w-full" data-testid="filter-state">
                  <SelectValue placeholder="All States" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="ALL">All States</SelectItem>
                  {INDIAN_STATES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">City</label>
              <Select
                value={searchFilters.city || 'ALL'}
                onValueChange={(v) =>
                  setSearchFilters({ ...searchFilters, city: v === 'ALL' ? '' : v })
                }
                disabled={!searchFilters.state}
              >
                <SelectTrigger className="w-full" data-testid="filter-city">
                  <SelectValue placeholder="All Cities" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="ALL">All Cities</SelectItem>
                  {availableCities.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!searchFilters.state && (
                <p className="text-xs text-slate-500 mt-1">Select state first</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Property Type</label>
              <Select
                value={searchFilters.type || 'ALL'}
                onValueChange={(v) =>
                  setSearchFilters({ ...searchFilters, type: v === 'ALL' ? '' : v })
                }
              >
                <SelectTrigger className="w-full" data-testid="filter-type">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Types</SelectItem>
                  <SelectItem value="Residential">Residential</SelectItem>
                  <SelectItem value="Agricultural">Agricultural</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                  <SelectItem value="Flat Rent">Flat Rent</SelectItem>
                  <SelectItem value="Flat Sell">Flat Sell</SelectItem>
                  <SelectItem value="House Rent">House Rent</SelectItem>
                  <SelectItem value="House Sell">House Sell</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <button
                onClick={resetFilters}
                className="w-full px-6 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg font-medium transition-colors"
              >
                Reset Filters
              </button>
            </div>
          </div>
          
          {(searchFilters.state || searchFilters.city || searchFilters.type) && (
            <div className="mt-4 text-sm text-slate-600">
              Showing {filteredProperties.length} of {properties.length} properties
            </div>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-6">
          {propertyTypes.map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                filter === type
                  ? 'bg-emerald-700 text-white shadow-lg shadow-emerald-500/30'
                  : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        {/* Must-have amenity filter chips — toggle any combo to narrow the grid */}
        <div
          className="flex flex-wrap items-center justify-center gap-2 mb-10 max-w-3xl mx-auto"
          data-testid="amenity-filter-row"
        >
          <span className="text-[11px] font-black uppercase tracking-widest text-slate-500 mr-1">
            Must-have:
          </span>
          {[
            { key: 'lift',            label: 'Lift',           emoji: '🛗' },
            { key: 'parking',         label: 'Parking',        emoji: '🚗' },
            { key: 'gym',             label: 'Gym',            emoji: '🏋️' },
            { key: 'swimming pool',   label: 'Pool',           emoji: '🏊' },
            { key: 'security',        label: '24×7 Security',  emoji: '🛡️' },
            { key: 'clubhouse',       label: 'Clubhouse',      emoji: '🎉' },
            { key: 'park',            label: 'Kids Park',      emoji: '🌳' },
            { key: 'power backup',    label: 'Power backup',   emoji: '🔋' },
          ].map(({ key, label, emoji }) => {
            const active = amenityFilters.has(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  const next = new Set(amenityFilters);
                  if (next.has(key)) next.delete(key); else next.add(key);
                  setAmenityFilters(next);
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold border transition-all ${
                  active
                    ? 'bg-emerald-700 text-white border-emerald-700 shadow-sm'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50'
                }`}
                data-testid={`amenity-filter-${key.replace(/\s+/g, '-')}`}
                aria-pressed={active}
              >
                <span aria-hidden="true">{emoji}</span> {label}
              </button>
            );
          })}
          {amenityFilters.size > 0 && (
            <button
              type="button"
              onClick={() => setAmenityFilters(new Set())}
              className="text-[11px] font-bold text-rose-700 hover:text-rose-900 underline ml-1"
              data-testid="amenity-filter-clear"
            >
              Clear
            </button>
          )}
        </div>

        {/* Properties Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProperties.map((property, index) => (
            <PropertyCard
              key={property.id}
              property={property}
              inquiryStats={inquiryStats}
              onView={openShareableProperty}
              index={index}
            />
          ))}
          </div>
        )}

        {/* Sticky post-grid action row: entry point back to Favourites + a
            second CTA for people who want to add their own land */}
        {!loading && filteredProperties.length > 0 && (
          <div
            className="mt-10 flex flex-wrap items-center justify-center gap-3 sm:gap-4"
            data-testid="projects-actions-row"
          >
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('am-open-favourites'))}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-rose-800 bg-rose-50 border border-rose-200 hover:bg-rose-100 hover:border-rose-300 transition-colors"
              data-testid="projects-open-favourites"
            >
              <Heart size={15} className="text-rose-600" fill="currentColor" />
              View my saved plots
            </button>
            <a
              href="#add-property-section"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-white bg-emerald-700 hover:bg-emerald-800 transition-colors shadow-md"
              data-testid="projects-list-your-land"
            >
              List your own land
            </a>
            <button
              type="button"
              onClick={() => setShowAdvisor(true)}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-emerald-800 bg-white border-2 border-emerald-700 hover:bg-emerald-50 transition-colors"
              data-testid="projects-talk-to-advisor"
            >
              Talk to an investment advisor
            </button>
          </div>
        )}

        {/* Property Details — custom portal modal (avoids Radix focus-trap
           conflict with the nested AppointmentModal). */}
        {selectedProperty && createPortal(
          <PropertyDetailPortal
            close={closeShareableProperty}
            onPrev={() => {
              const idx = filteredProperties.findIndex(p => p.id === selectedProperty.id);
              if (idx > 0) {
                const prev = filteredProperties[idx - 1];
                setSelectedProperty(prev);
                if (prev.property_code) navigate(`/property/${prev.property_code}`, { replace: true });
              }
            }}
            onNext={() => {
              const idx = filteredProperties.findIndex(p => p.id === selectedProperty.id);
              if (idx >= 0 && idx < filteredProperties.length - 1) {
                const next = filteredProperties[idx + 1];
                setSelectedProperty(next);
                if (next.property_code) navigate(`/property/${next.property_code}`, { replace: true });
              }
            }}
            canPrev={filteredProperties.findIndex(p => p.id === selectedProperty.id) > 0}
            canNext={(() => {
              const idx = filteredProperties.findIndex(p => p.id === selectedProperty.id);
              return idx >= 0 && idx < filteredProperties.length - 1;
            })()}
          >
              {/* Sticky top bar — always shows title, code and premium close */}
              <div className="am-property-modal-topbar">
                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-2 min-w-0">
                    <h2
                      id="am-property-modal-title"
                      className="text-lg sm:text-2xl font-black text-slate-900 leading-tight min-w-0 break-words"
                    >
                      {selectedProperty.title}
                    </h2>
                    {selectedProperty.property_code && (
                      <span
                        className="shrink-0 text-[10px] sm:text-[11px] font-mono font-bold px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 self-start"
                        data-testid="property-detail-code"
                      >
                        {selectedProperty.property_code}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap min-w-0">
                    <span className="flex items-center text-slate-600 text-xs sm:text-sm min-w-0 break-words">
                      <MapPin size={14} className="mr-1.5 text-emerald-700 shrink-0" />
                      <span className="min-w-0 break-words">{selectedProperty.location}</span>
                    </span>
                    <button
                      onClick={() => sharePropertyViaSystem(selectedProperty)}
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 border border-emerald-200 hover:bg-emerald-50 px-2.5 py-1 rounded-full transition-colors shrink-0"
                      data-testid="property-share-btn"
                    >
                      {shareCopied ? (
                        <>
                          <CheckCircle2 size={12} /> Link copied!
                        </>
                      ) : (
                        <>
                          <Share2 size={12} /> Share
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={closeShareableProperty}
                  className="am-property-modal-close"
                  aria-label="Close property details"
                  data-testid="property-detail-close"
                >
                  <X size={18} />
                  <span className="hidden sm:inline">Close</span>
                </button>
              </div>

              {/* Scrollable body */}
              <div className="am-property-modal-body">
                <div className="space-y-6 min-w-0">
                  {/* Image Gallery */}
                  <ImageGallery
                    mainImage={
                      selectedProperty.image.match(/^\/api\/(uploads|files)\//) 
                        ? `${BACKEND_URL}${selectedProperty.image}` 
                        : selectedProperty.image
                    }
                    images={(selectedProperty.images || []).map(img => 
                      img.match(/^\/api\/(uploads|files)\//) ? `${BACKEND_URL}${img}` : img
                    )}
                    title={selectedProperty.title}
                  />

                  {/* Videos Section */}
                  {selectedProperty.videos && selectedProperty.videos.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold text-slate-900">Property Videos</h3>
                      <div className="grid grid-cols-1 gap-4">
                        {selectedProperty.videos.map((video, index) => {
                          const raw = video.match(/^\/api\/(uploads|files)\//)
                            ? `${BACKEND_URL}${video}`
                            : video;
                          // Add #t=0.5 fragment so the browser scrubs to 0.5s
                          // and shows that frame as a "poster" without any
                          // separate upload. Works on every modern browser.
                          const posterSrc = /^https?:/i.test(raw)
                            ? raw + (raw.includes('#') ? '' : '#t=0.5')
                            : raw;
                          return (
                          <div key={video || `video-${index}`} className="relative bg-slate-900 rounded-xl overflow-hidden">
                            <video 
                              src={posterSrc}
                              controls
                              preload="metadata"
                              playsInline
                              className="w-full h-auto max-h-96"
                              onError={(e) => {
                                console.error('Video load error:', video);
                                // Safe: hide the broken video; no innerHTML injection.
                                if (e?.target) {
                                  e.target.style.display = 'none';
                                  const fallback = e.target.nextElementSibling;
                                  if (fallback) fallback.style.display = 'flex';
                                }
                              }}
                            >
                              Your browser does not support the video tag.
                            </video>
                            <div
                              style={{ display: 'none' }}
                              className="items-center justify-center h-48 bg-slate-800 text-white rounded-lg"
                            >
                              Video unavailable
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* --- Listing hero panel (modernized) --- */}
                  <div className="am-listing-hero" data-testid="property-hero-panel">
                    <div className="am-listing-hero-main">
                      <div className="am-listing-hero-label">Asking Price</div>
                      <div className="am-listing-hero-price">
                        <span className="am-listing-hero-cur">₹</span>
                        <span className="am-listing-hero-value">
                          {(selectedProperty.price || '').replace('₹', '').trim()}
                        </span>
                      </div>
                      <div className="am-listing-hero-perunits">
                        {selectedProperty.price_per_bigha && (
                          <span className="am-listing-hero-perunit">
                            ₹{Number(selectedProperty.price_per_bigha).toLocaleString('en-IN')}
                            <em>/bigha</em>
                          </span>
                        )}
                        {selectedProperty.price_per_katha && (
                          <span className="am-listing-hero-perunit">
                            ₹{Number(selectedProperty.price_per_katha).toLocaleString('en-IN')}
                            <em>/katha</em>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="am-listing-hero-side">
                      <div className="am-listing-hero-stat">
                        <Maximize2 size={14} className="am-listing-hero-stat-icon" />
                        <div>
                          <div className="am-listing-hero-stat-label">Plot Area</div>
                          <div className="am-listing-hero-stat-value">{selectedProperty.area}</div>
                        </div>
                      </div>
                      {selectedProperty.type && (
                        <div className="am-listing-hero-stat">
                          <Tag size={14} className="am-listing-hero-stat-icon" />
                          <div>
                            <div className="am-listing-hero-stat-label">Type</div>
                            <div className="am-listing-hero-stat-value">{selectedProperty.type}</div>
                          </div>
                        </div>
                      )}
                      {selectedProperty.status && (
                        <span className={`am-listing-hero-badge am-listing-hero-badge--${(selectedProperty.status || 'Available').toLowerCase()}`}>
                          <span className="am-listing-hero-badge-dot" />
                          {selectedProperty.status}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Embedded Google Map — only shown when the listing has coordinates or a maps URL */}
                  {(selectedProperty.latitude && selectedProperty.longitude) || selectedProperty.google_maps_url ? (
                    <div>
                      <h3 className="font-bold text-lg text-slate-900 mb-2 flex items-center gap-2">
                        <MapPin size={18} className="text-emerald-700" /> Location on Map
                      </h3>
                      <div className="rounded-xl overflow-hidden border border-slate-200 aspect-[16/9] bg-slate-100">
                        <iframe
                          title={`Map — ${selectedProperty.title}`}
                          src={
                            selectedProperty.latitude && selectedProperty.longitude
                              ? `https://maps.google.com/maps?q=${selectedProperty.latitude},${selectedProperty.longitude}&z=15&output=embed`
                              : `https://maps.google.com/maps?q=${encodeURIComponent(selectedProperty.location || selectedProperty.title)}&output=embed`
                          }
                          className="w-full h-full border-0"
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          allowFullScreen
                        />
                      </div>
                      {(() => {
                        // Build a bulletproof "Open in Google Maps" link.
                        // Preference: lat/lng first (always works) → user-pasted
                        // google_maps_url (only if it's an https:// URL) →
                        // location text as the search query.
                        let mapsHref = null;
                        if (selectedProperty.latitude && selectedProperty.longitude) {
                          mapsHref = `https://www.google.com/maps/search/?api=1&query=${selectedProperty.latitude},${selectedProperty.longitude}`;
                        } else if (
                          selectedProperty.google_maps_url &&
                          /^https?:\/\//i.test(selectedProperty.google_maps_url)
                        ) {
                          mapsHref = selectedProperty.google_maps_url;
                        } else if (selectedProperty.location) {
                          mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedProperty.location)}`;
                        }
                        return mapsHref ? (
                          <a
                            href={mapsHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-emerald-700 hover:text-emerald-900 underline"
                            data-testid="property-open-maps"
                          >
                            <MapPin size={12} /> Open in Google Maps
                          </a>
                        ) : null;
                      })()}
                    </div>
                  ) : null}

                  {/* Mouza map + AI locality intelligence — customer-facing */}
                  {selectedProperty.mouza_map_url && (
                    <MouzaMapCard property={selectedProperty} />
                  )}
                  {selectedProperty.latitude && selectedProperty.longitude && (
                    <LocalityReportCard property={selectedProperty} />
                  )}

                  {/* Description */}
                  <div>
                    <h3 className="font-bold text-lg text-slate-900 mb-2">Description</h3>
                    <p className="text-slate-600 leading-relaxed">{selectedProperty.description}</p>
                  </div>

                  {/* Features */}
                  <div>
                    <h3 className="font-bold text-lg text-slate-900 mb-3">Key Features</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedProperty.features.map((feature, index) => (
                        <div key={feature || `feature-${index}`} className="flex items-center text-slate-700">
                          <Tag size={16} className="mr-2 text-emerald-700" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  {!showInquiryForm ? (
                    <div className="space-y-3">
                      <button
                        onClick={() => {
                          // Open appointment modal directly. Do NOT clear
                          // selectedProperty here — that would trigger the
                          // Dialog's onOpenChange → closeShareableProperty,
                          // which navigates back to `/`. Instead we simply
                          // stack the appointment modal on top.
                          setAppointmentProperty(selectedProperty);
                          setShowAppointment(true);
                        }}
                        className="am-cta-primary w-full"
                        data-testid="property-book-appointment-btn"
                      >
                        <Calendar size={18} className="am-cta-icon" />
                        <span>Book Site Visit / Appointment</span>
                        <ArrowRight size={16} className="am-cta-arrow" />
                      </button>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <button
                          onClick={() => setShowInquiryForm(true)}
                          className="am-cta-secondary am-cta-inquire"
                          data-testid="property-cta-inquire"
                        >
                          <MessageCircle size={16} className="am-cta-icon" />
                          <span>Send Inquiry</span>
                        </button>
                        <a
                          href={getPhoneLink()}
                          className="am-cta-secondary am-cta-call"
                          data-testid="property-cta-call"
                        >
                          <Phone size={16} className="am-cta-icon" />
                          <span>Call Now</span>
                        </a>
                        <button
                          onClick={() => {
                            const message = `Hi! I'm interested in ${selectedProperty.title} located in ${selectedProperty.location}. Price: ${selectedProperty.price}, Area: ${selectedProperty.area}. Please provide more details.`;
                            openWhatsApp(message);
                          }}
                          className="am-cta-secondary am-cta-whatsapp"
                          data-testid="property-cta-whatsapp"
                        >
                          <MessageCircle size={16} className="am-cta-icon" />
                          <span>WhatsApp</span>
                        </button>
                      </div>

                      {/* Share Button */}
                      <button
                        onClick={() => handleShare(selectedProperty)}
                        className="am-cta-ghost w-full"
                        data-testid="property-cta-share"
                      >
                        <Share2 size={16} className="am-cta-icon" />
                        <span>Share Property</span>
                      </button>

                      {/* Printable QR code — for field-agent flyers */}
                      <PropertyQrCard property={selectedProperty} shareUrl={buildShareUrl(selectedProperty)} />
                    </div>
                  ) : (
                    <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                      <h3 className="font-bold text-lg text-slate-900">Send Inquiry</h3>
                      
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                        <input
                          type="text"
                          value={inquiryData.name}
                          onChange={(e) => setInquiryData({...inquiryData, name: e.target.value})}
                          placeholder="Enter your name"
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                        <input
                          type="email"
                          value={inquiryData.email}
                          onChange={(e) => setInquiryData({...inquiryData, email: e.target.value})}
                          placeholder="your.email@example.com"
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          value={inquiryData.phone}
                          onChange={(e) => setInquiryData({...inquiryData, phone: e.target.value})}
                          placeholder="+91 XXXXX XXXXX"
                          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>

                      <div className="flex gap-3">
                        <Button 
                          onClick={handleInquiry}
                          disabled={submitting}
                          className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white py-3 text-base font-semibold"
                        >
                          {submitting ? 'Sending...' : 'Submit Inquiry'}
                        </Button>
                        <Button 
                          onClick={() => setShowInquiryForm(false)}
                          variant="outline"
                          className="flex-1 py-3 text-base"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
          </PropertyDetailPortal>,
          document.body
        )}

        {/* Appointment modal launched from property details */}
        <AppointmentModal
          isOpen={showAppointment}
          onClose={() => {
            setShowAppointment(false);
            setAppointmentProperty(null);
          }}
          property={appointmentProperty}
        />

        {showAdvisor && <AdvisorCallModal onClose={() => setShowAdvisor(false)} />}
      </div>
    </section>
  );
};

